import React, {useEffect, useMemo, useRef, useState} from "react";
import ExcelJS from "exceljs";
import { formatTaskDateTime } from '../../utils/formatDate'
import Chart from "./components/Chart";
import ProjectList from "./components/ProjectList";
import TaskList from "./components/TaskList";
import DropDown from "../../components/DropDown";
import ProjectMembersPanel, {type ProjectMembersPanelSelectionMember} from "../../components/ProjectMembersPanel";
import styles from './index.module.scss'
import ProjectService from "../../services/project.service";
import WorkspaceMembersService from "../../services/workspace-members.service";
import {useWorkspace} from "../../context/workspace.context";
import {extractList} from "../../utils/members";
import UsersIcon from "../../assets/icon/UsersIcon";
import AllTasksIcon from "../../assets/icon/AllTasksIcon";
import CalendarSmallIcon from "../../assets/icon/CalendarSmallIcon";
import DotsVerticalIcon from "../../assets/icon/DotsVerticalIcon";
import StatisticsReportIcon from "../../assets/icon/StatisticsReportIcon";
import HideWidgetIcon from "../../assets/icon/HideWidgetIcon";
import WidgetDragHandleIcon from "../../assets/icon/WidgetDragHandleIcon";
import {getPeriodParams, type PeriodPreset} from "./periodParams";
import {PERIOD_OPTIONS, readStatisticsFilters, STATUS_OPTIONS, writeStatisticsFilters, type StatusFilter} from "./statisticsFilters";


const MyChart = ({onRemove, dragHandleProps}: {onRemove?: () => void; dragHandleProps?: any}) => {
    const { workspace } = useWorkspace()
    const [globalTaskList, setGlobalTaskList] = useState<any>([])
    const [staticData, setStatisticData] = useState<any>(null)
    const [activeProjects, setActiveProjects] = useState<any>([]);
    const [activeUser, setActiveUser] = useState<any>(null);
    const [allMembers, setAllMembers] = useState<any[]>([])
    const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([])
    // Фильтры восстанавливаются из куки при каждом заходе на страницу.
    const [savedFilters] = useState(readStatisticsFilters)
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(savedFilters.status)
    const [periodPreset, setPeriodPreset] = useState<PeriodPreset>(savedFilters.period)
    const [customPeriodStart, setCustomPeriodStart] = useState(savedFilters.customStart)
    const [customPeriodEnd, setCustomPeriodEnd] = useState(savedFilters.customEnd)
    const [periodDraftPreset, setPeriodDraftPreset] = useState<PeriodPreset>(savedFilters.period)
    const [customPeriodStartDraft, setCustomPeriodStartDraft] = useState(savedFilters.customStart)
    const [customPeriodEndDraft, setCustomPeriodEndDraft] = useState(savedFilters.customEnd)
    const [membersOpen, setMembersOpen] = useState(false)
    const [periodOpen, setPeriodOpen] = useState(false)
    const [memberPhotos, setMemberPhotos] = useState<Record<string, string>>({})
    const [assigneesInitialized, setAssigneesInitialized] = useState(false)
    const projectsInitializedRef = useRef(false)
    const statisticRequestRef = useRef(0)
    const statisticMembersRef = useRef<any[]>([])
    const assigneesInitializedRef = useRef(false)
    const assigneeSelectionTouchedRef = useRef(false)
    const membersRequestRef = useRef({settled: false, succeeded: false})
    const filterParams = useMemo(() => ({
        status_filter: statusFilter,
        ...getPeriodParams(periodPreset, customPeriodStart, customPeriodEnd),
    }), [customPeriodEnd, customPeriodStart, periodPreset, statusFilter])

    console.log(globalTaskList, "globalTaskList")

    useEffect(() => {
        writeStatisticsFilters({
            status: statusFilter,
            period: periodPreset,
            customStart: customPeriodStart,
            customEnd: customPeriodEnd,
        })
    }, [statusFilter, periodPreset, customPeriodStart, customPeriodEnd])

    const getStatisticData = async (params: any) => {
        const requestId = ++statisticRequestRef.current

        try {
            const response = await ProjectService.getProjectStatistic({
                workspace_id: workspace?.id,
                ...params,
            })

            if (requestId !== statisticRequestRef.current) return

            const availableProjects = Array.isArray(response?.available_projects)
                ? response.available_projects
                : []
            const responseMembers = Array.isArray(response?.members) ? response.members : []
            statisticMembersRef.current = responseMembers

            setStatisticData((currentData: any) => ({
                ...currentData,
                ...response,
                members: responseMembers,
                available_projects: currentData?.available_projects?.length
                    ? currentData.available_projects
                    : availableProjects,
            }))

            if (membersRequestRef.current.settled && !membersRequestRef.current.succeeded && !assigneesInitializedRef.current) {
                assigneesInitializedRef.current = true
                setAllMembers(responseMembers)
                if (!assigneeSelectionTouchedRef.current) {
                    setSelectedAssigneeIds(responseMembers.map((member: any) => String(member.user_id)))
                }
                setAssigneesInitialized(true)
            }

            if (!projectsInitializedRef.current && availableProjects.length) {
                projectsInitializedRef.current = true
                setActiveProjects(availableProjects.map((project: any) => project.id))
            }
        } catch (error) {
            console.error('Failed to load statistics:', error)
        }
    }

    useEffect(() => {
        projectsInitializedRef.current = false
        statisticRequestRef.current += 1
        setActiveProjects([])
        setActiveUser(null)
        setStatisticData(null)
        setAllMembers([])
        setSelectedAssigneeIds([])
        setMemberPhotos({})
        setAssigneesInitialized(false)
        statisticMembersRef.current = []
        assigneesInitializedRef.current = false
        assigneeSelectionTouchedRef.current = false
        membersRequestRef.current = {settled: false, succeeded: false}
    }, [workspace?.id])

    useEffect(() => {
        if (!workspace?.id) return

        let cancelled = false
        Promise.all([
            WorkspaceMembersService.getMembers(Number(workspace.id)),
            WorkspaceMembersService.getMemberPhotos(Number(workspace.id)).catch(() => null),
        ])
            .then(([membersResponse, photosResponse]) => {
                if (cancelled) return

                const photoMap = new Map<string, string>()
                extractList(photosResponse).forEach((photoItem: any) => {
                    const userId = String(photoItem.user_id ?? photoItem.user?.user_id ?? '')
                    const photo = photoItem.photo_data_url || photoItem.photo || photoItem.avatar
                    if (userId && photo) photoMap.set(userId, photo)
                })

                const workspaceMembers = extractList(membersResponse).map((member: any) => {
                    const userId = String(member.user_id ?? member.user?.user_id ?? member.user?.id ?? '')
                    const firstName = member.first_name ?? member.user?.first_name ?? ''
                    const lastName = member.last_name ?? member.user?.last_name ?? ''
                    const email = member.email || member.user?.email || ''
                    const role = member.role_in_workspace ?? member.role ?? member.user?.role
                    const avatar = photoMap.get(userId)
                        || member.photo_data_url
                        || member.photo
                        || member.avatar
                        || member.user?.photo
                        || member.user?.avatar
                        || ''
                    return {
                        ...(member.user || {}),
                        ...member,
                        id: userId,
                        user_id: userId,
                        first_name: firstName,
                        last_name: lastName,
                        full_name: `${firstName} ${lastName}`.trim() || email || userId,
                        email,
                        role,
                        role_in_workspace: member.role_in_workspace ?? role,
                        roles: Array.isArray(member.roles)
                            ? member.roles
                            : Array.isArray(member.user?.roles)
                                ? member.user.roles
                                : role ? [role] : [],
                        highest_role: member.highest_role ?? member.user?.highest_role ?? role,
                        avatar,
                        photo: avatar,
                    }
                }).filter((member: any) => member.user_id)

                setMemberPhotos(Object.fromEntries(photoMap))

                membersRequestRef.current = {settled: true, succeeded: true}
                assigneesInitializedRef.current = true
                setAllMembers(workspaceMembers)
                if (!assigneeSelectionTouchedRef.current) {
                    setSelectedAssigneeIds(workspaceMembers.map((member: any) => String(member.user_id)))
                }
                setAssigneesInitialized(true)
            })
            .catch((error) => {
                if (cancelled) return
                console.error('Failed to load statistic members:', error)
                membersRequestRef.current = {settled: true, succeeded: false}
                const fallbackMembers = statisticMembersRef.current
                if (fallbackMembers.length) {
                    assigneesInitializedRef.current = true
                    setAllMembers(fallbackMembers)
                    if (!assigneeSelectionTouchedRef.current) {
                        setSelectedAssigneeIds(fallbackMembers.map((member: any) => String(member.user_id)))
                    }
                    setAssigneesInitialized(true)
                }
            })

        return () => {
            cancelled = true
        }
    }, [workspace?.id])

    useEffect(() => {
        if (!workspace?.id) return

        if (projectsInitializedRef.current && activeProjects.length === 0) {
            statisticRequestRef.current += 1
            setActiveUser(null)
            setStatisticData((currentData: any) => ({
                ...currentData,
                members: (currentData?.members || []).map((member: any) => ({
                    ...member,
                    tasks_count: 0,
                })),
            }))
            return
        }

        getStatisticData({
            project_ids: activeProjects,
            ...filterParams,
        })
    }, [workspace?.id, activeProjects, filterParams])

    const statusTitle = STATUS_OPTIONS.find((option) => option.id === statusFilter)?.title || 'Все задачи'
    const periodTitle = PERIOD_OPTIONS.find((option) => option.id === periodPreset)?.title || 'Месяц'
    const selectionMembers = useMemo<ProjectMembersPanelSelectionMember[]>(() => allMembers.map((member) => {
        const userId = String(member.user_id)
        return {
            id: member.id ?? userId,
            userId,
            name: member.full_name || member.email || userId,
            email: member.email || '',
            photo: memberPhotos[userId],
        }
    }), [allMembers, memberPhotos])
    const taskAssigneeIds = useMemo(
        () => activeUser == null ? selectedAssigneeIds : [String(activeUser)],
        [activeUser, selectedAssigneeIds],
    )

    const getExelReport = () => {
        const data = globalTaskList.map( task => {
            const assignee =  task.assignee_ids.map((asigneeId: string) => selectionMembers.find((user) => user.id === asigneeId)?.name).join(", ")
            const creator =  selectionMembers.find((user) => user.id === task.created_by_id)?.name



            return {
                space: workspace?.name,
                list: task.project.name,
                assignee: assignee,
                taskName: task.title,
                description: task.description,
                status: task.status,
                creator: creator,
                priority: task.priority,
                url: window.location.origin,
                created: formatTaskDateTime(task.created_at),
                closed: "-",
                done: "-",
                archived: task.is_archived,
                tags: "-",
                attachments: task.attachments_count,
                distributed: "-",
                spent: task.time_spent,
                estimated: 0,
            }
        })

        const saveFile = (buffer: any, fileName: any) => {
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        };

        const exportExcel = async () => {
            const totalSpent = data.reduce((sum, item) => sum + item.spent, 0);
            const totalEstimated = data.reduce((sum, item) => sum + item.estimated, 0);

            const grandTotal = totalSpent + totalEstimated;

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Tasks");

            // Заголовки
            sheet.columns = [
                { header: "Space", key: "space", width: 25 },
                { header: "List", key: "list", width: 15 },
                { header: "Assignee", key: "assignee", width: 25 },
                { header: "Task name", key: "taskName", width: 30 },
                { header: "Task description", key: "description", width: 40 },
                { header: "Task status", key: "status", width: 15 },
                { header: "Creator", key: "creator", width: 25 },
                { header: "Priority", key: "priority", width: 10 },
                { header: "Url", key: "url", width: 30 },
                { header: "Date created", key: "created", width: 15 },
                { header: "Date closed", key: "closed", width: 15 },
                { header: "Date done", key: "done", width: 15 },
                { header: "Archived", key: "archived", width: 10 },
                { header: "Tags", key: "tags", width: 15 },
                { header: "Attachments", key: "attachments", width: 15 },
                { header: "Is Distributed", key: "distributed", width: 15 },
                { header: "Assignee estimated time", key: "estimated", width: 25 },
                { header: "Assignee spent time", key: "spent", width: 20 },
            ];

            data.forEach(item => {
                sheet.addRow(item);
            });

            const yellowCells = ["A1", "C1", "D1", "E1", "F1", "I1", "L1", "O1"];

            yellowCells.forEach(cellAddress => {
                const cell = sheet.getCell(cellAddress);

                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFFFFF00" }, // желтый
                };

                cell.font = { bold: true };
            });

            const assigneeSpentTime = data.reduce((accum: any, item: any) => accum + +item.spent, 0);
            const assigneeEstimatedTime = data.reduce((accum: any, item: any) => accum + +item.estimated, 0);


            sheet.getCell(`D${data.length + 2}`).value = "Total";
            sheet.getCell(`D${data.length + 3}`).value = "Grand Total";

            sheet.getCell(`R${data.length + 2}`).value = assigneeSpentTime;
            sheet.getCell(`R${data.length + 3}`).value = assigneeSpentTime + assigneeEstimatedTime;

            const cell = sheet.getCell(`R${data.length + 3}`);

            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFF00" }, // желтый
            };

            cell.font = { bold: true };


            // Сохранение (нативный JS)
            const buffer = await workbook.xlsx.writeBuffer();

            saveFile(new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }), "users.xlsx");
        };

        exportExcel()
    }

    const selectChartUser = (userId: string | number | null) => {
        const normalizedId = userId == null ? '' : String(userId)
        setActiveUser(normalizedId || null)
    }

    const resetAssigneeSelection = () => {
        assigneeSelectionTouchedRef.current = false
        setActiveUser(null)
        setSelectedAssigneeIds(allMembers.map((member: any) => String(member.user_id)))
    }

    const setPeriodDropdownOpen = (isOpen: boolean) => {
        if (isOpen) {
            setPeriodDraftPreset(periodPreset)
            setCustomPeriodStartDraft(customPeriodStart)
            setCustomPeriodEndDraft(customPeriodEnd)
        }
        setPeriodOpen(isOpen)
    }

    const customPeriodInvalid = Boolean(
        customPeriodStartDraft
        && customPeriodEndDraft
        && customPeriodStartDraft > customPeriodEndDraft
    )

    const applyCustomPeriod = () => {
        if (customPeriodInvalid) return
        setCustomPeriodStart(customPeriodStartDraft)
        setCustomPeriodEnd(customPeriodEndDraft)
        setPeriodPreset('custom')
        setPeriodOpen(false)
    }

    if(!staticData){
        return null
    }

    return (
        <div className={styles.statistics}>
            <div className={styles.statistics__header}>
                <div className={styles.statistics__title}>
                    <span className={styles.statistics__drag} aria-label="Переместить виджет" {...dragHandleProps}><WidgetDragHandleIcon/></span>
                    <h3>Статистика</h3>
                </div>
                <div className={styles.statistics__filters}>
                    <button type="button" className={styles.statistics__filterButton} onClick={getExelReport}>
                        <span>Сформировать отчет</span>
                    </button>

                    <button type="button" className={styles.statistics__filterButton} onClick={() => setMembersOpen(true)}>
                        <UsersIcon color="#64617E"/>
                        <span>Участники</span>
                    </button>

                    <DropDown
                        position="right"
                        closeDropDownByPick
                        dropdownClassName={styles.statistics__dropdown}
                        btn={(isOpen) => (
                            <button type="button" className={styles.statistics__filterButton}>
                                <AllTasksIcon/>
                                <span>{statusTitle}</span>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d={isOpen ? 'M3 7.5 6 4.5l3 3' : 'm3 4.5 3 3 3-3'} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                        )}
                    >
                        <div className={styles.statistics__dropdownList}>
                            {STATUS_OPTIONS.map((option) => (
                                <button key={option.id} type="button" className={statusFilter === option.id ? styles.active : ''} onClick={() => setStatusFilter(option.id)}>{option.title}</button>
                            ))}
                        </div>
                    </DropDown>

                    <DropDown
                        position="right"
                        isOpen={periodOpen}
                        setIsOpen={setPeriodDropdownOpen}
                        dropdownClassName={styles.statistics__dropdown}
                        btn={(isOpen) => (
                            <button type="button" className={styles.statistics__filterButton}>
                                <CalendarSmallIcon/>
                                <span>{periodTitle}</span>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d={isOpen ? 'M3 7.5 6 4.5l3 3' : 'm3 4.5 3 3 3-3'} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                        )}
                    >
                        <div className={styles.statistics__dropdownList}>
                            {PERIOD_OPTIONS.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={periodDraftPreset === option.id ? styles.active : ''}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setPeriodDraftPreset(option.id)
                                        if (option.id !== 'custom') {
                                            setPeriodPreset(option.id)
                                            setPeriodOpen(false)
                                        }
                                    }}
                                >{option.title}</button>
                            ))}
                            {periodDraftPreset === 'custom' && (
                                <div className={styles.statistics__customPeriod} onClick={(event) => event.stopPropagation()}>
                                    <label>С <input type="date" value={customPeriodStartDraft} onChange={(event) => setCustomPeriodStartDraft(event.target.value)}/></label>
                                    <label>По <input type="date" value={customPeriodEndDraft} onChange={(event) => setCustomPeriodEndDraft(event.target.value)}/></label>
                                    {customPeriodInvalid && <span className={styles.statistics__customPeriodError}>Дата начала должна быть раньше даты окончания</span>}
                                    <button type="button" disabled={customPeriodInvalid} onClick={applyCustomPeriod}>Применить</button>
                                </div>
                            )}
                        </div>
                    </DropDown>

                    {onRemove && (
                        <DropDown
                            position="right"
                            closeDropDownByPick
                            dropdownClassName={styles.statistics__actionsDropdown}
                            contentClassName={styles.statistics__actionsContent}
                            btn={(
                                <button type="button" className={styles.statistics__more} aria-label="Действия с виджетом">
                                    <DotsVerticalIcon/>
                                </button>
                            )}
                        >
                            <div className={styles.statistics__actionsList}>
                                <button type="button" disabled title="Формирование отчёта пока недоступно">
                                    <StatisticsReportIcon/>
                                    <span>Сформировать в отчёт</span>
                                </button>
                                <button type="button" onClick={onRemove}>
                                    <HideWidgetIcon/>
                                    <span>Скрыть виджет</span>
                                </button>
                            </div>
                        </DropDown>
                    )}
                </div>
            </div>
            <div className={styles.statistics__content}>
                <div className={styles.chart__block}>
                    <Chart
                        chartsData={staticData?.members || []}
                        tasks={globalTaskList}
                        activeUser={activeUser}
                        setActiveUser={selectChartUser}
                    />
                </div>
                <div className={`${styles.chart__block} ${styles.chart__blockProjects}`}>
                    <div className={styles.chart__projectListFrame}>
                        <ProjectList projects={staticData?.available_projects || []} activeProjects={activeProjects} setActiveUser={resetAssigneeSelection} setActiveProjects={setActiveProjects}/>
                    </div>
                </div>
            </div>
            <div>
                <div className={`${styles.chart__block} ${styles.full}`}>
                    <TaskList
                        setGlobalTaskList={setGlobalTaskList}
                        activeProjects={activeProjects}
                        assigneeIds={taskAssigneeIds}
                        assigneesInitialized={assigneesInitialized}
                        filterParams={filterParams}
                        projects={staticData?.available_projects || []}
                        projectUsers={allMembers}
                    />
                </div>
            </div>

            <ProjectMembersPanel
                isOpen={membersOpen}
                onClose={() => setMembersOpen(false)}
                project={null}
                selectionMembers={selectionMembers}
                selectedMemberIds={selectedAssigneeIds}
                onSelectionChange={(selectedIds) => {
                    assigneeSelectionTouchedRef.current = true
                    setSelectedAssigneeIds(selectedIds)
                    setActiveUser(selectedIds.length === 1 ? selectedIds[0] : null)
                }}
                compact
            />
        </div>
    );
};

export default MyChart;
